import React from 'react';
import { GqlMenuSection } from 'types/graphql';
import { useFlags } from 'launchdarkly-react-client-sdk';

import usePaths from 'src/hooks/use-paths';
import useUI from 'src/hooks/use-ui';
import { ProductRecommendationSection } from 'src/utils/analytics/trackers/internal-gtm-tracker/internal-gtm-tracker.types';
import { ProductCarousel } from 'src/components/product-carousel';
import { ProductCarousel as ProductCarouselV2 } from 'src/components/carousel-v2/types/product-carousel';
import { HighlightCard } from 'src/components/product-card-v2/highlight-card';
import { LockIconSimple } from 'src/assets/lock-icon-simple';
import { useCollectionCarousel } from './use-collection-carousel';

export type CollectionCarouselProps = {
  section?: GqlMenuSection;
  sectionIndex?: number;
  showLink?: boolean;
  showTitle?: boolean;
};

export function CollectionCarousel(props: CollectionCarouselProps): JSX.Element | null {
  const { section, showLink, showTitle } = props;
  const { collectionId, label: sectionLabel, sortBy } = section ?? {};
  const { isEmbeddedCarousel } = useUI();
  const label = 'Collection Products';

  const flags = useFlags();
  const isProductCardsV2Enabled = flags['growth.ecomm.product-card-v2.rollout'] ?? false;

  const carouselProps = useCollectionCarousel({
    collectionId: collectionId ?? '',
    sortBy: sortBy ?? '',
  });

  const { products, loading, cardBadgeColor, collectionDisplayMode, slug } = carouselProps;
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  const { href: collectionHref } = usePaths({ collections: slug || collectionId || '' });

  // Don't render if exclusive collection is hidden (no access or feature flag disabled)
  if (collectionDisplayMode === 'hidden') {
    return null;
  }

  if (!loading && !products.length) {
    return null;
  }
  const exclusiveCollectionCard = collectionDisplayMode === 'exclusive' && (
    <HighlightCard
      title='Exclusive Collection'
      icon={<LockIconSimple color='currentColor' />}
      href={collectionHref}
      isEmbeddedCarousel={isEmbeddedCarousel}
      message={
        <>
          You're in! <br />
          Check out select products inside.
        </>
      }
      customColor={cardBadgeColor}
      testId='exclusive-highlight-card'
    />
  );

  if (isProductCardsV2Enabled) {
    return (
      <ProductCarouselV2
        beforeProducts={exclusiveCollectionCard}
        products={products}
        isLoading={loading}
        title={sectionLabel}
        href={collectionHref}
        hideTitle={showTitle === false}
        hideLink={showLink === false}
      />
    );
  }

  return (
    <ProductCarousel
      heading={sectionLabel ?? label}
      trackerSection={ProductRecommendationSection.collectionItems}
      {...carouselProps}
    />
  );
}
